import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import {
  getAllDealsV2,
  getGetAllDealsV2QueryKey,
} from "@vega-api/generated/deal/deal";
import { type DealResponseV2 } from "@vega-api/generated/types/dealResponseV2";
import { SearchableDropdown } from "@vega-core/components/Form/Controls/SearchableDropdown/SearchableDropdown";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";
import { useDebounce } from "@vega-core/utils/useDebounce";

import { SEARCH_DEBOUNCE_DELAY_MS } from "../../constants/searchConstants";

const DEALS_DROPDOWN_PAGE_SIZE = 50;
const ALL_DEALS_VALUE = "__all_deals__";

type Props = {
  value?: string;
  onChange: (dealId: string | undefined) => void;
};

/**
 * Picks a co-invest deal to filter a list (notifications, messages) by, plus
 * an "All deals" option that clears the filter. Unlike `CoInvestDealsDropdown`
 * it does not navigate — it only reports the selected deal id to the caller.
 */
export const NotificationsDealFilterDropdown = ({ value, onChange }: Props) => {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(
    searchQuery,
    SEARCH_DEBOUNCE_DELAY_MS,
  );

  const {
    data: dealsPages,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      getGetAllDealsV2QueryKey(),
      "notifications-filter",
      debouncedSearchQuery,
    ],
    queryFn: ({ pageParam }) =>
      getAllDealsV2({
        page: pageParam,
        pageSize: DEALS_DROPDOWN_PAGE_SIZE,
        searchQuery: debouncedSearchQuery || undefined,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.meta?.hasNextPage ? lastPageParam + 1 : undefined,
  });

  const deals: DealResponseV2[] =
    dealsPages?.pages.flatMap((page) => page.data ?? []) ?? [];

  const allDealsOption: OptionType = {
    label: m.coInvestments_notifications_allDeals(),
    value: ALL_DEALS_VALUE,
  };
  const options: OptionType[] = [
    allDealsOption,
    ...deals.map((deal) => ({ label: deal.name || "-", value: deal.id })),
  ];

  const selectedOption =
    (value ? options.find((option) => option.value === value) : undefined) ??
    allDealsOption;

  return (
    <SearchableDropdown
      className="w-full sm:w-80"
      hasNextPage={hasNextPage}
      inputValue={searchQuery}
      isLoading={isLoading}
      isLoadingMore={isFetchingNextPage}
      name="coinvestNotificationsDealFilter"
      onChange={(option) =>
        onChange(
          option && option.value !== ALL_DEALS_VALUE ? option.value : undefined,
        )
      }
      onInputChange={setSearchQuery}
      onLoadMore={fetchNextPage}
      options={options}
      placeholder={m.coInvestments_notifications_filterByDeal()}
      shouldFilter={false}
      value={selectedOption}
      withCheckmark
      withChevron
      wrapOptionLabels
    />
  );
};
